import { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CircularProgress from '@mui/material/CircularProgress';
import { useQuery } from '@tanstack/react-query';
import type { SegmentAggregatedCardsResponse } from '@trinity/chat/types/segment-aggregated-data';
import {
  BUCKET_COLORS,
  SEGMENT_CARD_BUCKETS,
  SEGMENT_CARD_DEFINITIONS,
  getSegmentCardDefinitions,
  type SegmentCard,
  type SegmentCardBucket,
} from '@trinity/chat/types/segment-card';
import { getSegmentAggregatedData } from '../api/client';
import ExecutiveSummary from './ExecutiveSummary';
import { CardCitationsModal } from './Modal.CardCitations';
import { EmbeddedCardChart } from './SegmentCharts';

type EmbeddedChart = React.ComponentProps<typeof EmbeddedCardChart>['chart'];

/**
 * DOM id for a card/chart source. Mirrors the anchor hash produced by
 * `buildExecutiveSummarySources` on the backend (camelCase → kebab-case).
 * Used by ExecutiveSummary footer links to scroll into view.
 */
export function cardAnchorId(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1-$2')
    .toLowerCase();
}

function getChartsForCardKey(key: string, item: SegmentAggregatedCardsResponse): EmbeddedChart[] {
  const charts: EmbeddedChart[] = [];
  switch (key) {
    case 'cardArchetype':
      if (item.chartArchetypeDistribution) {
        charts.push({ kind: 'archetype', data: item.chartArchetypeDistribution });
      }
      break;
    case 'card1lIntensityRegimen':
      if (item.chartRegimenTypeMix) {
        charts.push({ kind: 'regimen', data: item.chartRegimenTypeMix });
      }
      break;
    case 'cardAntiCd38Behavior':
      if (item.chartAntiCd38Split) {
        charts.push({ kind: 'antiCd38', data: item.chartAntiCd38Split });
      }
      if (item.chartTolerabilityRanking) {
        charts.push({ kind: 'tolerabilityRanking', data: item.chartTolerabilityRanking });
      }
      break;
    case 'cardPatientDecisionStyle':
      if (item.chartDecisionMakingStyle) {
        charts.push({ kind: 'decision', data: item.chartDecisionMakingStyle });
      }
      break;
    case 'cardConstraints':
      if (item.chartTopConstraints) {
        charts.push({ kind: 'constraints', data: item.chartTopConstraints });
      }
      break;
    case 'cardPatientMix':
      if (item.chartPatientMix) {
        charts.push({ kind: 'patientMix', data: item.chartPatientMix });
      }
      break;
    case 'cardEvidenceOrientation':
      if (item.chartEndpointImportance) {
        charts.push({ kind: 'endpointImportance', data: item.chartEndpointImportance });
      }
      // if (item.chartRegimenChoiceDrivers) {
      //   charts.push({ kind: 'regimenChoiceDrivers', data: item.chartRegimenChoiceDrivers });
      // }
      break;
    case 'cardDurationSequencing':
      if (item.chartDeescalationOrder) {
        charts.push({ kind: 'deescalationOrder', data: item.chartDeescalationOrder });
      }
      break;
    case 'cardInformationPreferences':
      if (item.chartTopInformationSources) {
        charts.push({ kind: 'topInformationSources', data: item.chartTopInformationSources });
      }
      break;
  }
  return charts;
}

interface SegmentQualInsightsProps {
  segmentData: Record<string, unknown>;
  segmentName: string;
  editMode?: boolean;
}

export default function SegmentQualInsights({
  segmentData,
  segmentName,
}: SegmentQualInsightsProps): React.JSX.Element | null {
  const [expanded, setExpanded] = useState(true);

  const segmentId = segmentData.id as number | undefined;

  const { data, isLoading } = useQuery({
    queryKey: ['segment-aggregated-data', segmentId],
    queryFn: () => getSegmentAggregatedData(segmentId as number),
    enabled: segmentId != null,
  });

  const item = data?.item;

  const buckets = useMemo(() => {
    if (!item) return [];
    const cardDefs = getSegmentCardDefinitions(segmentName);

    return SEGMENT_CARD_BUCKETS.map((bucket) => {
      const cards = cardDefs.filter((def) => def.bucket === bucket)
        .map((def) => {
          const val = item[def.key as keyof typeof item] as unknown as SegmentCard | null | undefined;
          if (!val || !val.headline) return null;
          const charts = getChartsForCardKey(def.key, item);
          return { ...def, card: val, charts };
        })
        .filter((c): c is NonNullable<typeof c> => c != null);

      return { bucket, cards };
    }).filter((b) => b.cards.length > 0);
  }, [item, segmentName]);

  if (!isLoading && buckets.length === 0) {
    return null;
  }

  return (
    <Root>
      <Header onClick={() => setExpanded(!expanded)}>
        <span>HCP Attributes and Traits</span>
        <ExpandIcon $expanded={expanded}>
          <ExpandMoreIcon />
        </ExpandIcon>
      </Header>
      {expanded && (
        <Content>
          {isLoading ? (
            <LoadingContainer>
              <CircularProgress size={28} />
            </LoadingContainer>
          ) : (
            <>
              {segmentId != null && <ExecutiveSummary segmentId={segmentId} />}
              {buckets.map(({ bucket, cards }) => (
                <CollapsibleBucket key={bucket} bucket={bucket} cards={cards} segmentId={segmentId!} />
              ))}
            </>
          )}
        </Content>
      )}
    </Root>
  );
}

// ─── Collapsible bucket ─────────────────────────────────────────────────────

function CollapsibleBucket({
  bucket,
  cards,
  segmentId,
}: {
  bucket: SegmentCardBucket;
  cards: { key: string; label: string; card: SegmentCard; charts: EmbeddedChart[] }[];
  segmentId: number;
}): React.JSX.Element {
  const [open, setOpen] = useState(true);
  const [citationsCardKey, setCitationsCardKey] = useState<string | null>(null);
  const [citationsLabel, setCitationsLabel] = useState('');

  return (
    <BucketSection>
      <BucketHeader onClick={() => setOpen(!open)} style={{ cursor: 'pointer' }}>
        <BucketTitle>{bucket}</BucketTitle>
        <BucketExpandIcon $expanded={open}>
          <ExpandMoreIcon fontSize="small" />
        </BucketExpandIcon>
      </BucketHeader>
      {open && (
        <CardGrid>
          {cards.map(({ key, label, card, charts }) => (
            <Card key={key} id={cardAnchorId(key)}>
              <CardTopRow>
                <CardLabel>{label}</CardLabel>
                {/* <CardBucketPill $bucket={bucket}>{bucket}</CardBucketPill> */}
              </CardTopRow>
              <CardHeadline>{card.headline}</CardHeadline>
              {charts.map((chart, i) => (
                <EmbeddedCardChart key={i} chart={chart} />
              ))}
              <CardSummary>{card.summary}</CardSummary>
              {card.tags.length > 0 && (
                <TagList>
                  {card.tags.map((tag) => {
                    const colonIdx = tag.indexOf(':');
                    return (
                      <Tag key={tag} $bucket={bucket}>
                        {colonIdx !== -1 ? (
                          <>
                            <strong>{tag.slice(0, colonIdx)}</strong>:{tag.slice(colonIdx + 1)}
                          </>
                        ) : (
                          tag
                        )}
                      </Tag>
                    );
                  })}
                </TagList>
              )}
              <CardFooter>
                <CitationsLink
                  onClick={() => {
                    setCitationsCardKey(key);
                    setCitationsLabel(label);
                  }}
                >
                  View Citations
                </CitationsLink>
              </CardFooter>
            </Card>
          ))}
        </CardGrid>
      )}
      {citationsCardKey && (
        <CardCitationsModal
          open
          onClose={() => setCitationsCardKey(null)}
          segmentId={segmentId}
          cardKey={citationsCardKey}
          cardLabel={citationsLabel}
        />
      )}
    </BucketSection>
  );
}

// ─── Styled components ──────────────────────────────────────────────────────

const Root = styled.div`
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  margin-bottom: 12px;
`;

const Header = styled.div`
  padding: 8px 16px;
  font-size: 18px;
  font-weight: 700;
  color: #323232;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  user-select: none;
  min-height: 48px;
  box-sizing: border-box;
`;

const ExpandIcon = styled.span<{ $expanded: boolean }>`
  color: rgba(0, 0, 0, 0.54);
  transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
  transform: rotate(${(p) => (p.$expanded ? '180deg' : '0deg')});
  display: flex;
`;

const Content = styled.div`
  padding: 0 12px 12px;
  background: var(--blue-xl);
  padding-top: 12px;
  border-top: 1px solid var(--gray-xl);
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0;
`;

const BucketSection = styled.div`
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const BucketHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
`;

const BucketTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: var(--black-l, #323232);
`;

const BucketBadge = styled.span<{ $bucket: SegmentCardBucket }>`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  color: ${(p) => BUCKET_COLORS[p.$bucket].text};
  background: ${(p) => BUCKET_COLORS[p.$bucket].bg};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 12px;
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 10px;
  padding: 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  scroll-margin-top: 220px;
`;

const CardTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const CardLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.45);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CardBucketPill = styled.span<{ $bucket: SegmentCardBucket }>`
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 8px;
  color: ${(p) => BUCKET_COLORS[p.$bucket].text};
  background: ${(p) => BUCKET_COLORS[p.$bucket].bg};
`;

const BucketExpandIcon = styled.span<{ $expanded: boolean }>`
  color: rgba(0, 0, 0, 0.4);
  transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1);
  transform: rotate(${(p) => (p.$expanded ? '180deg' : '0deg')});
  display: flex;
`;

const CardHeadline = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: var(--black-l, #323232);
  line-height: 1.3;
  margin-bottom: 6px;
`;

const CardSummary = styled.div`
  font-size: 13px;
  line-height: 1.5;
  color: rgba(0, 0, 0, 0.65);
  margin-bottom: 12px;
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
`;

const Tag = styled.span<{ $bucket: SegmentCardBucket }>`
  font-size: 12px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 14px;
  // color: ${(p) => BUCKET_COLORS[p.$bucket].text};
  // background: ${(p) => BUCKET_COLORS[p.$bucket].bg};
  background: #e5f5fc;
  white-space: nowrap;
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
`;

const CitationsLink = styled.button`
  all: unset;
  font-size: 13px;
  font-weight: 600;
  color: #1271c4;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;
